import {StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {useForceUpdate, View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {MediaLayout, Strut} from "@khanacademy/wonder-blocks-layout";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    HeadingLarge,
    HeadingMedium,
    LabelMedium,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";
import {
    enums,
    usePublishAnalyticsEvent,
    withConversionContext,
} from "@khan/analytics-learning-time";
import {getPromptVariable} from "@khan/guide-core-helpers";
import {useGuideState} from "@khan/guide-state";
import type {RequestedInput as RequestedInputType} from "@khan/guide-types";
import {RequestedInput} from "@khan/guide-ui";
import type {KAError} from "@khan/logging";

import type {Tool} from "#coach-dashboard-package/khanmigo/ai-tools-list.ts";

import SubmissionErrorBanner from "./submission-error-banner.tsx";

type Props = {
    tool: Tool;
    loading: boolean;
    submitText: string;
    inputs: readonly RequestedInputType[];
    submitError?: KAError;
    submitting?: boolean;
    onSubmit: () => void;
};

function UserInputs(props: Props): React.ReactElement {
    const {
        tool,
        submitText,
        inputs,
        loading,
        submitting,
        submitError,
        onSubmit,
    } = props;

    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    const threadId = useGuideState("threadId");
    const navigation = useGuideState("navigation");

    // NOTE(reid): This is a small hack to make the message bubble re-render
    // when one of the requested input fields changes (so that we disable
    // and re-enable the action bubbles if necessary).
    const rerender = useForceUpdate();

    const isAnyRequiredInputMissing = inputs.some((i) => {
        const promptVariable = getPromptVariable(i.variableName) || "";
        return (
            i.required &&
            typeof promptVariable === "string" &&
            promptVariable.trim().length < 1
        );
    });

    const handleClick = () => {
        publishAnalyticsEvent({
            eventType: "AiGuideCoeditorGenerateClicked",
            eventSchemaVersion: 1,
            aiGuideThreadId: threadId ?? "",
            aiGuideNavigationId: navigation.id,
        });

        onSubmit();
    };

    return (
        <MediaLayout>
            {({mediaSize}) => (
                <View style={styles.container}>
                    {mediaSize === "small" ? (
                        <View
                            style={[
                                styles.header,
                                styles.headerSmall,
                                {background: tool.color},
                            ]}
                        >
                            <HeadingMedium>{tool.title}</HeadingMedium>
                            <Strut size={spacing.xSmall_8} />
                            <LabelSmall style={styles.description}>
                                {tool.description}
                            </LabelSmall>
                        </View>
                    ) : (
                        <View style={[styles.header, {background: tool.color}]}>
                            <PhosphorIcon icon={tool.icon} size="large" />
                            <Strut size={spacing.small_12} />
                            <HeadingLarge>{tool.title}</HeadingLarge>
                            <Strut size={spacing.small_12} />
                            <LabelMedium style={styles.description}>
                                {tool.description}
                            </LabelMedium>
                        </View>
                    )}

                    {loading ? (
                        <CircularSpinner size="medium" style={styles.loading} />
                    ) : (
                        <View style={styles.inputs} testId="ai-tool-form">
                            {inputs.map((input, index) => (
                                <RequestedInput
                                    input={input}
                                    onChange={rerender}
                                    key={index}
                                />
                            ))}

                            {tool.footer && (
                                <View>
                                    {tool.footer}
                                    <Strut size={spacing.large_24} />
                                </View>
                            )}

                            {submitError && (
                                <View>
                                    <SubmissionErrorBanner
                                        submitError={submitError}
                                    />
                                    <Strut size={spacing.xSmall_8} />
                                </View>
                            )}

                            <Button
                                disabled={
                                    isAnyRequiredInputMissing || submitting
                                }
                                type="submit"
                                onClick={handleClick}
                                spinner={submitting}
                            >
                                {submitText}
                            </Button>
                        </View>
                    )}
                </View>
            )}
        </MediaLayout>
    );
}

export const styles = StyleSheet.create({
    container: {
        borderRadius: spacing.xxxSmall_4,
        background: color.white,
        width: "100%",
    },
    header: {
        padding: spacing.xLarge_32,
        borderTopLeftRadius: spacing.xxxSmall_4,
        borderTopRightRadius: spacing.xxxSmall_4,
        borderBottom: `1px solid ${color.offBlack16}`,
        alignItems: "center",
        flexShrink: 0,
    },
    headerSmall: {
        padding: `${spacing.medium_16}px ${spacing.large_24}px`,
    },
    description: {
        width: "100%",
        maxWidth: spacing.medium_16 * 28,
        textAlign: "center",
    },
    inputs: {
        width: "100%",
        maxWidth: spacing.medium_16 * 31,
        padding: spacing.xLarge_32,
        margin: `0 auto`,
        flexShrink: 0,
    },
    loading: {
        margin: spacing.xLarge_32,
        alignSelf: "center",
    },
});

export default compose(
    withConversionContext({
        activity: enums.Activity.AI_GUIDE_ACTIVITY,
    }),
)(UserInputs) as React.ComponentType<Props>;
